import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import "./StoreMultiSelect.css";

/**
 * Выбор магазинов для отчётных страниц (AUFB-65).
 *
 * Раньше здесь стоял нативный `<select multiple>`: он занимал высоту в шесть строк,
 * не показывал, что именно выбрано, без прокрутки, и требовал Ctrl+клик — на 49
 * магазинах это неудобно. Здесь обычный дропдаун: кнопка со сводкой выбора,
 * поиск по названию, чекбоксы и «Все магазины».
 *
 * Контролируемый компонент: выбранные id живут у родителя массивом строк,
 * пустой массив = все магазины (так же трактует и бэкенд).
 */
export default function StoreMultiSelect({ stores, value, onChange, label = "Магазины" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);
  const searchRef = useRef(null);

  const selected = useMemo(() => (Array.isArray(value) ? value.map(String) : []), [value]);
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  // Закрытие по клику вне и по Esc — иначе дропдаун висит поверх страницы.
  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  // Фокус в поиск при открытии — на 49 магазинах искать быстрее, чем листать.
  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
  }, [open]);

  const storeName = useCallback((store) => store.name || `Магазин #${store.id}`, []);

  const filtered = useMemo(() => {
    const list = Array.isArray(stores) ? stores : [];
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((s) => storeName(s).toLowerCase().includes(q));
  }, [stores, query, storeName]);

  const toggle = useCallback(
    (storeId) => {
      const id = String(storeId);
      if (selectedSet.has(id)) {
        onChange(selected.filter((x) => x !== id));
      } else {
        onChange([...selected, id]);
      }
    },
    [onChange, selected, selectedSet]
  );

  const selectAll = useCallback(() => onChange([]), [onChange]);

  const selectFiltered = useCallback(() => {
    // Выбираем только то, что сейчас видно в поиске, не теряя выбранное раньше.
    const merged = new Set(selected);
    filtered.forEach((s) => merged.add(String(s.id)));
    onChange(Array.from(merged));
  }, [filtered, onChange, selected]);

  const summary = useMemo(() => {
    if (selected.length === 0) return "Все магазины";
    if (selected.length === 1) {
      const one = (stores || []).find((s) => String(s.id) === selected[0]);
      return one ? storeName(one) : "1 магазин";
    }
    // 2-4 — «магазина», остальное — «магазинов».
    const tail = selected.length % 10;
    const teen = selected.length % 100 >= 11 && selected.length % 100 <= 14;
    const word = !teen && tail >= 2 && tail <= 4 ? "магазина" : "магазинов";
    return `${selected.length} ${word}`;
  }, [selected, stores, storeName]);

  return (
    <div className="store-multiselect" ref={wrapRef}>
      {label && <label className="store-multiselect__label">{label}</label>}

      <button
        type="button"
        className={`store-multiselect__trigger${open ? " is-open" : ""}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={`store-multiselect__summary${selected.length ? " is-selected" : ""}`}>{summary}</span>
        <span className="store-multiselect__caret" aria-hidden="true">
          ▾
        </span>
      </button>

      {open && (
        <div className="store-multiselect__menu" role="listbox">
          <div className="store-multiselect__search-row">
            <input
              ref={searchRef}
              type="text"
              className="store-multiselect__search"
              placeholder="Поиск магазина…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <div className="store-multiselect__actions">
            <button type="button" className="store-multiselect__action" onClick={selectAll}>
              Все магазины
            </button>
            {query.trim() && filtered.length > 0 && (
              <button type="button" className="store-multiselect__action" onClick={selectFiltered}>
                Выбрать найденные ({filtered.length})
              </button>
            )}
          </div>

          <div className="store-multiselect__list">
            {filtered.length === 0 ? (
              <div className="store-multiselect__empty">Ничего не найдено</div>
            ) : (
              filtered.map((store) => {
                const id = String(store.id);
                const checked = selectedSet.has(id);
                return (
                  <label key={store.id} className="store-multiselect__option">
                    <input type="checkbox" checked={checked} onChange={() => toggle(store.id)} />
                    <span className="store-multiselect__option-text">{storeName(store)}</span>
                  </label>
                );
              })
            )}
          </div>

          {selected.length > 0 && (
            <div className="store-multiselect__footer">
              Выбрано: {selected.length}
              <button type="button" className="store-multiselect__clear" onClick={selectAll}>
                Сбросить
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
